import Field from '../../components/Field';
import { TRIAL_DAY_OPTIONS, type TrialDays, type UpdateTrialPayload } from '../../types';

/** Employees included with every station — mirrors the platform-wide users_per_station. */
export const USERS_PER_STATION = 5;

/** Form state for the trial block — strings so a cleared field never collapses to Number('') === 0. */
export interface TrialFormState {
  trialDays: string;
  stationQuota: string;
  addonUserQuota: string;
}

export const defaultTrialForm = (init: Partial<TrialFormState> = {}): TrialFormState => ({
  trialDays: '30',
  stationQuota: '1',
  addonUserQuota: '0',
  ...init,
});

/** Parses a required integer text field, or returns an Indonesian error message. */
export function parseIntField(raw: string, label: string, min: number): { value: number } | { error: string } {
  const trimmed = raw.trim();
  const num = Number(trimmed);
  if (trimmed === '' || !Number.isInteger(num) || num < min) {
    return { error: `${label} harus bilangan bulat minimal ${min}.` };
  }
  return { value: num };
}

export function parseTrialForm(form: TrialFormState): { value: UpdateTrialPayload } | { error: string } {
  const station = parseIntField(form.stationQuota, 'Kuota titik absen', 1);
  if ('error' in station) return station;
  const addon = parseIntField(form.addonUserQuota, 'Pegawai tambahan', 0);
  if ('error' in addon) return addon;
  const days = Number(form.trialDays) as TrialDays;
  if (!TRIAL_DAY_OPTIONS.some((o) => o.value === days)) return { error: 'Durasi trial tidak valid.' };
  return { value: { station_quota: station.value, addon_user_quota: addon.value, trial_days: days } };
}

/** Live "Total kursi pegawai" line; tolerant of half-typed numbers (falls back to 0). */
export const seatSummary = (form: TrialFormState) => {
  const station = Number(form.stationQuota) || 0;
  const addon = Number(form.addonUserQuota) || 0;
  return `Total kursi pegawai: ${station * USERS_PER_STATION + addon} (${USERS_PER_STATION} per titik absen)`;
};

const TrialFields = ({ form, onChange }: { form: TrialFormState; onChange: (next: TrialFormState) => void }) => {
  const set = (key: keyof TrialFormState) => (value: string) => onChange({ ...form, [key]: value });
  return (
    <>
      <Field label="Durasi trial">
        <select className="w-full border rounded px-3 py-2" value={form.trialDays} onChange={(e) => set('trialDays')(e.target.value)}>
          {TRIAL_DAY_OPTIONS.map((o) => (
            <option key={o.value} value={String(o.value)}>{o.label}</option>
          ))}
        </select>
      </Field>
      <Field label="Kuota titik absen">
        <input type="number" min={1} step={1} className="w-full border rounded px-3 py-2" value={form.stationQuota} onChange={(e) => set('stationQuota')(e.target.value)} />
      </Field>
      <Field label="Pegawai tambahan">
        <input type="number" min={0} step={1} className="w-full border rounded px-3 py-2" value={form.addonUserQuota} onChange={(e) => set('addonUserQuota')(e.target.value)} />
      </Field>
      <div className="text-xs text-gray-600 -mt-1 mb-3">{seatSummary(form)}</div>
    </>
  );
};
export default TrialFields;
